/* eslint-disable */

import React, { useEffect, useRef } from 'react';
import { string, func, bool, oneOfType, object } from 'prop-types';
import { Modal, message, Button } from 'antd';
import I18nCustomFormatter from 'Src/common/components/i18nCustomFormatter';
import ShareBlock from 'Src/common/components/ShareBlock';
import ThemeXShareMenu from 'Src/common/components/themeXShareMenu';
import { getMetaContent, copyToClipboard } from './../../utilities/dom_utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare } from '@fortawesome/pro-solid-svg-icons';
import { faLink } from '@fortawesome/pro-light-svg-icons';
import { faFacebook, faLinkedin, faWhatsapp, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import './style.scss';
import { announceToScreenReader, trapModalFocus } from 'Src/common/utilities/a11y_util';

const ogImage = getMetaContent('og:image');

function ThemeXShareModal({
  className,
  visible,
  url,
  title,
  description,
  twitterShareText,
  closeText,
  handleClose,
  dashboardUrl,
  dashboardAltMsg,
  shareBlockText,
  hideViewDashboard,
  goBack,
}) {
  const instanceId = useRef(Math.random().toString(36).substr(2, 8)).current;
  const headingId = `share-modal-heading-${instanceId}`;
  const announcementRegionId = `themex-share-announcement-${instanceId}`;
  const shareMenuId = `themex-share-menu-${instanceId}`;
  const modalWrapClassName = `themex-share-modal-wrap-${instanceId}`;
  const focusTimer = useRef(null);

  useEffect(() => {
    if (!visible) return undefined;

    let releaseFocusTrap = () => {};

    focusTimer.current = setTimeout(() => {
      const modalWrap = document.querySelector(`.${modalWrapClassName}`);
      const dialog = modalWrap && (
        modalWrap.getAttribute('role') === 'dialog'
          ? modalWrap
          : modalWrap.querySelector('[role="dialog"]')
      );
      if (dialog) {
        dialog.setAttribute('tabindex', '-1');
        dialog.focus();
        releaseFocusTrap = trapModalFocus(dialog.querySelector('.ant-modal'));
      }
    }, 100);

    return () => {
      clearTimeout(focusTimer.current);
      focusTimer.current = null;
      releaseFocusTrap();
    };
  }, [visible, modalWrapClassName]);

  let facebookUrl = '';
  let twitterUrl = '';
  if (url) {
    facebookUrl = new URL(url);
    twitterUrl = new URL(url);
    facebookUrl.searchParams.set('utm_medium', 'facebook');
    twitterUrl.searchParams.set('utm_medium', 'twitter');
  }
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${facebookUrl}&display=popup`;
  const twUrl = `http://twitter.com/share?text=${twitterShareText}&url=${encodeURIComponent(twitterUrl)}`;

  function copyLink(url) {
    copyToClipboard(url);
    message.success('Copied link to clipboard');
    announceToScreenReader('Link copied to clipboard!', announcementRegionId);
  }

  const containerClassName = `themex-share-modal ${className}`;
  function handleShareDropdownVisibleChange(nextVisible) {
    announceToScreenReader(
      `Share dropdown ${nextVisible ? 'expanded' : 'collapsed'}`,
      announcementRegionId,
    );
  }

  function openShareUrl(shareUrl) {
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  }

  function handleGoBackKeyDown(event) {
    if (
      event.key !== 'Tab'
      || event.shiftKey
      || event.altKey
      || event.ctrlKey
      || event.metaKey
    ) {
      return;
    }

    const modalWrap = event.currentTarget.closest(`.${modalWrapClassName}`);
    const closeButton = modalWrap && modalWrap.querySelector('.ant-modal-close');
    if (closeButton && closeButton.getClientRects().length) {
      event.preventDefault();
      closeButton.focus();
    }
  }

  const shareItems = [
    {
      key: 'copy-link',
      label: 'Copy Link',
      icon: <FontAwesomeIcon icon={faLink} className="mr8" />,
      onClick: () => copyLink(url),
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: <FontAwesomeIcon icon={faFacebook} className="mr8" />,
      onClick: () => openShareUrl(fbUrl),
    },
    {
      key: 'twitter',
      label: 'X.com',
      icon: <FontAwesomeIcon icon={faXTwitter} className="mr8" />,
      onClick: () => openShareUrl(twUrl),
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: <FontAwesomeIcon icon={faWhatsapp} className="mr8" />,
      onClick: () => openShareUrl(`https://wa.me/?text=${encodeURIComponent(url)}`),
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: <FontAwesomeIcon icon={faLinkedin} className="mr8" />,
      onClick: () => openShareUrl(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`),
    },
  ];

  return (
    <Modal
      visible={visible}
      className={containerClassName}
      width="25rem"
      closable={true}
      maskClosable={false}
      keyboard={false}
      footer={null}
      wrapClassName={modalWrapClassName}
      wrapProps={{ 'aria-modal': 'true', 'aria-labelledby': headingId }}
      onCancel={() => handleClose()}>
      <div className="themex-share-modal-header">
        <h1 id={headingId} className="arc-H300 title">
          {title}
        </h1>
        <p className="arc-p arc-color-B45">{description}</p>
      </div>
      <If condition={shareBlockText}>
        <div className="share-block-wrapper">
          <ShareBlock title={shareBlockText} url={url} accessibilityContrast />
        </div>
      </If>

      <div
        className="themex-share-back arc-card-box-shadow"
        style={{ backgroundImage: `url(${staticPath}img/react-modules/share_og_back.png)` }}>
        <img src={ogImage} className="themex-share-image" alt="Preview of your social share post" />
      </div>
      <div>
        <ThemeXShareMenu
          items={shareItems}
          menuId={shareMenuId}
          onVisibleChange={handleShareDropdownVisibleChange}
          triggerIcon={<FontAwesomeIcon className="mr8" icon={faShare} />}
          triggerLabel={<I18nCustomFormatter id="giving-checkout-share-text" />}
        />
      </div>
      <div className="themex-share-links">
        <If condition={dashboardUrl || dashboardAltMsg}>
          <Choose>
            <When condition={dashboardUrl}>
              <Button
                type="link"
                shape="round"
                href={dashboardUrl}
                tabIndex="0"
                className="dashboard-link mt16"
                target="_blank">
                View my dashboard
              </Button>
            </When>
            <Otherwise>
              <div className="alt-block">{dashboardAltMsg}</div>
            </Otherwise>
          </Choose>
        </If>
      </div>
      <Button
        onClick={() => handleClose()}
        onKeyDown={handleGoBackKeyDown}
        tabIndex="0"
        className="close-action arc-btn-subtle mt16 arc-p arc-color-B20">
        {goBack ? goBack : 'Go Back'}
      </Button>
      <div
        className="visually-hidden-a11y-div"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        id={announcementRegionId}
      />
    </Modal>
  );
}

ThemeXShareModal.propTypes = {
  visible: bool.isRequired,
  title: oneOfType([string, object]),
  description: oneOfType([string, object]),
  closeText: string,
  url: string,
  handleClose: func.isRequired,
  twitterShareText: string,
};

ThemeXShareModal.defaultProps = {
  title: 'Invite a friend',
  description: '',
  closeText: 'Close',
  url: `${window.location.origin}${window.location.pathname}`,
  twitterShareText: '',
};

export default ThemeXShareModal;
